/**
 * Where you were.
 *
 * The hash router has no notion of "back": clicking a name from a party takes
 * you to the profile, and then there is nowhere obvious to return to. The
 * browser's own history would do it, except that the desktop shell has no
 * back button to press and hash writes from code pile up entries that lead
 * back through screens nobody chose to visit.
 *
 * So the trail is kept here. It records screens as they change, ignores
 * repeats, and forgets the deep past - fifty entries is far more than anyone
 * walks back through, and it keeps a long session from growing without end.
 */

import { useEffect, useState } from 'react';
import { parseHash, go } from './hash';

/*
 * Five, and only five.
 *
 * Deep history nobody asked for is a liability: a back button that can walk
 * you through forty screens is not a way back, it is a maze. Five is what the
 * hover list offers and five is all that is kept - six here because the
 * screen you are standing on takes one of the slots and is never something
 * you can go 'back' to.
 */
const LIMIT = 6;
const trail: string[] = [];
const listeners = new Set<() => void>();

/** True while going back, so the resulting hash change is not recorded. */
let rewinding = false;

function currentPath(): string {
  return location.hash.replace(/^#\/?/, '') || 'home';
}

function announce(): void {
  for (const fn of listeners) fn();
}

/** Records the current screen. Called once per hash change. */
export function record(): void {
  if (rewinding) { rewinding = false; announce(); return; }
  const here = currentPath();
  if (trail[trail.length - 1] === here) return;
  trail.push(here);
  if (trail.length > LIMIT) trail.shift();
  announce();
}

/**
 * The trail behind you, nearest first, for the hover list.
 *
 * Never more than five: the trail holds six and the last of them is where
 * you already are.
 */
export function behind(): string[] {
  return trail.slice(0, -1).reverse();
}

/** Whether there is anywhere to go back to. */
export function canGoBack(): boolean {
  return trail.length > 1;
}

/** The screen back would return to, for a tooltip that says where. */
export function previous(): string | null {
  return trail.length > 1 ? trail[trail.length - 2] ?? null : null;
}

/** Goes back one screen. Does nothing at the start of the trail. */
export function back(): void {
  if (trail.length < 2) return;
  trail.pop();
  const to = trail[trail.length - 1];
  if (!to) return;
  /*
   * Only claim to be rewinding when the address is actually going to change.
   * Setting the hash to what it already is fires no hashchange, so the flag
   * would still be up when you next navigated for real - and that screen
   * would go unrecorded.
   */
  if (to !== currentPath()) rewinding = true;
  go(to);
  announce();
}

/**
 * A readable name for a path, for the tooltip on the back button.
 * Deliberately coarse - "Party" is enough, the party's name is not worth a
 * lookup here.
 */
export function label(p: string | null): string {
  if (!p) return 'Back';
  return 'Back to ' + name(p);
}

/** The readable name of a screen on its own, for a row in the hover list. */
export function name(p: string): string {
  return SCREENS[parseHash('#/' + p).head]?.label ?? parseHash('#/' + p).head;
}

/** The icon for a screen, so the back list is not a column of bare words. */
export function icon(p: string): string {
  return SCREENS[parseHash('#/' + p).head]?.icon ?? 'dice';
}

const SCREENS: Record<string, { label: string; icon: string }> = {
  home: { label: 'Home', icon: 'home' },
  games: { label: 'Games', icon: 'dice' },
  parties: { label: 'Parties', icon: 'squads' },
  party: { label: 'Party', icon: 'squads' },
  friends: { label: 'Friends', icon: 'friends' },
  chat: { label: 'Messages', icon: 'chat' },
  notifications: { label: 'Notifications', icon: 'bell' },
  settings: { label: 'Settings', icon: 'sliders' },
  u: { label: 'Profile', icon: 'profile' },
  badges: { label: 'Badges', icon: 'badges' },
  leaderboard: { label: 'Leaderboard', icon: 'badges' },
  staff: { label: 'Staff console', icon: 'shieldNav' }
};

/** Re-renders a component whenever the trail changes. */
export function useHistory(): {
  canBack: boolean;
  back: () => void;
  hint: string;
  /** Where you have been, nearest first, at most five. */
  trail: { path: string; label: string; icon: string }[];
  /** Jumps straight to one of them, dropping everything in between. */
  goBackTo: (path: string) => void;
} {
  const [, tick] = useState(0);
  useEffect(() => {
    const fn = () => tick((n) => n + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return {
    canBack: canGoBack(),
    back,
    hint: label(previous()),
    trail: behind().map((path) => ({ path, label: name(path), icon: icon(path) })),
    goBackTo
  };
}

/**
 * Goes back to a particular screen in the trail.
 *
 * Everything nearer than it is dropped: picking the third entry means you
 * went back three, not that you visited it again.
 */
export function goBackTo(path: string): void {
  const at = trail.lastIndexOf(path);
  if (at < 0 || at === trail.length - 1) return;
  trail.length = at + 1;
  if (path !== currentPath()) rewinding = true;
  go(path);
  announce();
}

/** Starts recording. Called once, from the shell. */
export function watchHistory(): () => void {
  record();
  window.addEventListener('hashchange', record);
  return () => window.removeEventListener('hashchange', record);
}
